/**
 * The list under a focused time field: what the typed text will become (or a
 * hint when it is not a time), then every half hour to pick from.
 */
import { useEffect, useRef } from 'react';

import { TIME_SLOTS } from './time-of-day';
import { TIME_SLOT_HEIGHT, TimeSlotOption } from './TimeSlotOption';
import { TimeTypedHint } from './TimeTypedHint';

export interface TimeSlotMenuProps {
  id: string;
  /** The slot to highlight and scroll to. */
  current: number;
  /** What the typed text reads as; undefined while nothing is typed. */
  typed: number | null | undefined;
  width: number;
  onPick: (minutes: number) => void;
}

export function TimeSlotMenu({ id, current, typed, width, onPick }: TimeSlotMenuProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const slots = TIME_SLOTS.includes(current)
    ? TIME_SLOTS
    : [...TIME_SLOTS, current].sort((x, y) => x - y);
  const scrollIndex = slots.findIndex((s) => s >= current);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = Math.max(0, scrollIndex * TIME_SLOT_HEIGHT - 75);
    }
  }, [scrollIndex]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        width: Math.max(width, 168),
        zIndex: 30,
        background: 'var(--nb-bg)',
        borderRadius: 10,
        boxShadow: '0 12px 28px -8px rgba(0,0,0,.2), 0 0 0 1px var(--nb-border)',
        overflow: 'hidden',
      }}
    >
      {typed !== undefined && <TimeTypedHint typed={typed} />}
      <div
        id={id}
        role="listbox"
        ref={listRef}
        style={{ maxHeight: 210, overflow: 'auto', padding: 4 }}
      >
        {slots.map((s) => (
          <TimeSlotOption key={s} minutes={s} selected={s === current} onPick={onPick} />
        ))}
      </div>
      <div
        aria-hidden="true"
        style={{
          padding: '7px 11px',
          borderTop: '1px solid var(--nb-border)',
          fontSize: 10.5,
          color: 'var(--nb-subtle)',
          background: 'var(--nb-surface)',
        }}
      >
        Type a time · ↑ ↓ moves 15 min
      </div>
    </div>
  );
}
